import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ShelterUpdate } from "@/types/shelterUpdate";
import type { Shelter } from "@/types/shelter";

export interface ShelterHistoryEntry extends ShelterUpdate {
  shelter_current_name?: string;
  originalShelter?: Shelter;
  original_values: Record<string, any> | null;
}

export const useShelterHistory = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["shelter-history"],
    queryFn: async (): Promise<ShelterHistoryEntry[]> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return [];
      }

      // Get all non-pending updates (approved and rejected)
      const { data: updates, error } = await supabase
        .from("shelter_updates")
        .select("*")
        .in("status", ["approved", "rejected"])
        .order("reviewed_at", { ascending: false })
        .limit(100);

      if (error) {
        console.error("Error fetching shelter history:", error);
        throw new Error(error.message);
      }

      if (!updates || updates.length === 0) {
        return [];
      }

      // Get unique shelter IDs
      const shelterIds = [...new Set(
        updates
          .filter((u) => u.shelter_id !== null)
          .map((u) => u.shelter_id as string)
      )];

      // Fetch full shelter data for comparison
      let sheltersMap: Record<string, Shelter> = {};
      if (shelterIds.length > 0) {
        const { data: shelters, error: sheltersError } = await supabase
          .from("shelters")
          .select("*")
          .in("id", shelterIds);

        if (!sheltersError && shelters) {
          sheltersMap = shelters.reduce((acc, s) => {
            acc[s.id] = {
              id: s.id,
              name: s.name,
              city: s.city,
              region: s.region,
              address: s.address,
              postal_code: s.postal_code ?? null,
              lat: s.lat,
              lng: s.lng,
              phone: s.phone,
              email: s.email,
              website: s.website,
              current_needs: s.current_needs,
              urgent_needs: s.urgent_needs,
              urgent_needs_set_at: s.urgent_needs_set_at,
              dog_walking_available: s.dog_walking_available,
              volunteering_available: s.volunteering_available,
              dogs_available: s.dogs_available,
              cats_available: s.cats_available,
              volunteer_info: s.volunteer_info,
              description: s.description,
              last_updated: s.last_updated ?? s.created_at,
              created_at: s.created_at,
              data_verified: s.data_verified ?? false,
              organization_verified: (s as any).organization_verified ?? false,
              organization_verified_at: (s as any).organization_verified_at ?? null,
              organization_verification_pending: (s as any).organization_verification_pending ?? false,
              organization_verification_requested_at: (s as any).organization_verification_requested_at ?? null,
              contacted: (s as any).contacted ?? false,
            };
            return acc;
          }, {} as Record<string, Shelter>);
        }
      }

      return updates.map((u) => ({
        ...u,
        urgent_needs: u.urgent_needs ?? false,
        dog_walking_available: u.dog_walking_available ?? false,
        volunteering_available: u.volunteering_available ?? false,
        dogs_available: u.dogs_available ?? true,
        cats_available: u.cats_available ?? true,
        status: u.status as 'pending' | 'approved' | 'rejected',
        source: ((u as any).source || 'public') as any,
        shelter_current_name: u.shelter_id ? sheltersMap[u.shelter_id]?.name : undefined,
        originalShelter: u.shelter_id ? sheltersMap[u.shelter_id] : undefined,
        original_values: (u as any).original_values ?? null,
      })) as ShelterHistoryEntry[];
    },
    enabled,
  });
};

export const useShelterUpdatesByShelter = (shelterId: string | null, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["shelter-updates-by-shelter", shelterId],
    queryFn: async (): Promise<ShelterUpdate[]> => {
      if (!shelterId) return [];

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return [];
      }

      const { data: updates, error } = await supabase
        .from("shelter_updates")
        .select("*")
        .eq("shelter_id", shelterId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching shelter updates:", error);
        throw new Error(error.message);
      }

      return (updates || []).map((u) => ({
        ...u,
        urgent_needs: u.urgent_needs ?? false,
        dog_walking_available: u.dog_walking_available ?? false,
        volunteering_available: u.volunteering_available ?? false,
        dogs_available: u.dogs_available ?? true,
        cats_available: u.cats_available ?? true,
        status: u.status as 'pending' | 'approved' | 'rejected',
        source: ((u as any).source || 'public') as any,
      })) as ShelterUpdate[];
    },
    enabled: enabled && !!shelterId,
  });
};
